import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import {
  LOCATION_KINDS,
  locationKindEffect,
  locationKindLabel,
  locationKindOf,
  type LocationKind,
} from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { StockLocationOption } from './Purchasing.js'

/**
 * Where a shop says stock may be put. §6.30 `BR-04`, `BR-10`; §6.10 `REF-010`.
 *
 * **A shop could not create a stock location at all.** `insert into stock_location` appeared once
 * outside tests, in `tools/seed`: no route, no service, no screen. Measured on the deployment before
 * this slice, all seven tenants held one branch and **zero** locations. `receive` needs a
 * `locationId` and `framesForSale` reads only selling locations, so a shop that signed up could not
 * take one frame into stock or sell one. The same shape as the catalogue hole: every read built, and
 * the one write that feeds them missing.
 *
 * **The kind is a chooser, not three checkboxes.** `BR-04`'s central warehouse and `REF-010`'s trial
 * case are both non-selling and are mutually exclusive, and two checkboxes let a shopkeeper tick
 * both and meet a refusal. Four kinds, one of which is the ordinary case, and the consequence of
 * each is written under the chooser rather than left to be discovered.
 *
 * Every write goes through the queue (`OFF-001a`). The server refuses a sellable trial case or
 * warehouse as a 422 sentence rather than letting the check constraint answer 23514, because a 500
 * in the queue is retried forever with every write behind it blocked.
 */

const flagsFor = (
  kind: LocationKind,
): { readonly isSelling: boolean; readonly isCentralWarehouse: boolean; readonly isTrial: boolean } =>
  kind === 'selling'
    ? { isSelling: true, isCentralWarehouse: false, isTrial: false }
    : kind === 'back_store'
      ? { isSelling: false, isCentralWarehouse: false, isTrial: false }
      : kind === 'warehouse'
        ? { isSelling: false, isCentralWarehouse: true, isTrial: false }
        : { isSelling: false, isCentralWarehouse: false, isTrial: true }

export interface StockLocationsProps {
  readonly branchId: string
  /**
   * Appendix B's `Branch - create, close`, owner only.
   *
   * There is no location row in the matrix. A location is part of a branch's structure rather than
   * a stock movement, and one nobody meant to create is where stock silently lands.
   */
  readonly canEdit: boolean
  readonly loadLocations: () => Promise<readonly StockLocationOption[]>
  readonly onWritten?: () => void
}

export function StockLocations({
  branchId,
  canEdit,
  loadLocations,
  onWritten,
}: StockLocationsProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [locations, setLocations] = useState<readonly StockLocationOption[]>([])
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [kind, setKind] = useState<LocationKind>('selling')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLocations(await loadLocations())
    } catch {
      // A list that cannot be read is not a reason to hide the form: the whole point of this screen
      // is that a shop with no locations has to be able to make the first one.
      setLocations([])
    }
  }, [loadLocations])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * The form is cleared when the write settles, not when the button is pressed.
   *
   * `OFF-001a`'s queue can refuse a write minutes later, and a form cleared on submit leaves the
   * shopkeeper with a refusal and nothing to correct.
   */
  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('stockLocations.saved', { name }))
    setName('')
    setCode('')
    setKind('selling')
    void refresh()
    onWritten?.()
  })

  const save = useCallback(async () => {
    if (name.trim() === '' || code.trim() === '') {
      setError(t('stockLocations.nameAndCodeRequired'))
      return
    }
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/stock/locations',
      label: t('stockLocations.queueLabel', { name: name.trim() }),
      body: {
        branchId,
        name: name.trim(),
        // Upper-cased by the server too. Done here so the duplicate warning below matches what the
        // server will compare, rather than telling a shopkeeper a code is free when it is not.
        code: code.trim().toUpperCase(),
        ...flagsFor(kind),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, code, enqueue, kind, name, t])

  if (!canEdit) {
    return (
      <section className="lm-setup-panel" aria-labelledby="stock-locations-heading">
        <h2 id="stock-locations-heading" className="lm-setup-panel__heading">
          {t('stockLocations.heading')}
        </h2>
        {/* A form whose Save is a 403 would halt the write queue with everything behind it
            (`OFF-001a`). */}
        <p className="lm-hint">{t('stockLocations.notYours')}</p>
      </section>
    )
  }

  const taken = locations.some((l) => l.code === code.trim().toUpperCase())

  return (
    <section className="lm-setup-panel" aria-labelledby="stock-locations-heading">
      <h2 id="stock-locations-heading" className="lm-setup-panel__heading">
        {t('stockLocations.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('stockLocations.means')}</p>

      <div>
        <h3 className="lm-field__label">{t('stockLocations.existingHeading')}</h3>
        {locations.length === 0 ? (
          // The state every tenant on the server was actually in, said plainly rather than shown as
          // an empty box: a shop with no location cannot receive stock at all.
          <p className="lm-hint">{t('stockLocations.noneYet')}</p>
        ) : (
          <ul className="lm-location-list">
            {locations.map((l) => (
              <li key={l.id} className="lm-location-row">
                <span className="lm-location-row__name" dir="auto">
                  {l.name}
                </span>
                {/* `LOC-005`: a code is Latin whatever the page direction. */}
                <span className="lm-location-row__meta" dir="ltr">
                  {l.code}
                </span>
                {/* The kind is a word and never a colour: `CL-02`, and a non-selling location
                    being invisible at the counter is too large a thing to say with a swatch. */}
                <span className="lm-location-row__meta">{locationKindLabel(t, locationKindOf(l))}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <h3 className="lm-field__label">{t('stockLocations.newHeading')}</h3>

        <label className="lm-field">
          <span className="lm-field__label">{t('stockLocations.name')}</span>
          <input
            className="lm-input"
            dir="auto"
            value={name}
            autoComplete="off"
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('stockLocations.code')}</span>
          {/* `LOC-005`: the code goes on a receiving screen and a shelf label, so it is Latin. */}
          <input
            className="lm-input"
            dir="ltr"
            value={code}
            autoComplete="off"
            onChange={(e) => setCode(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('stockLocations.kindLabel')}</span>
          {/* One chooser rather than three checkboxes: a warehouse and a trial case are mutually
              exclusive and both non-selling, so a pair of ticks can only produce a refusal. */}
          <select
            className="lm-input"
            value={kind}
            onChange={(e) => setKind(e.currentTarget.value as LocationKind)}
          >
            {LOCATION_KINDS.map((value) => (
              <option key={value} value={value}>
                {locationKindLabel(t, value)}
              </option>
            ))}
          </select>
        </label>

        {/* What choosing it does, under the chooser. A non-selling location is invisible at the
            counter, which is the kind of consequence a shopkeeper should not have to discover. */}
        <p className="lm-setup-panel__effects">{locationKindEffect(t, kind)}</p>

        {taken && code.trim() !== '' ? (
          <p className="lm-hint" role="status">
            {t('stockLocations.codeTaken', { code: code.trim().toUpperCase() })}
          </p>
        ) : null}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save()
        }}
      >
        {t('stockLocations.save')}
      </button>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('stockLocations.queueSubject'))}
        </p>
      )}
    </section>
  )
}
